import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Mail, Send, Eye, Smartphone, Monitor, Save,
  Palette, FileText, Settings, ChevronRight, Image, AlertCircle
} from 'lucide-react';

// ─── Template List ───────────────────────────────────────
function TemplateList({ templates, onPreview, onTestSend, sending }) {
  return (
    <div className="space-y-2" data-testid="template-list">
      {templates.map(t => (
        <div key={t.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors" data-testid={`template-${t.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-sm text-slate-900">{t.name}</p>
              <Badge variant="secondary" className="text-[10px] bg-green-50 text-green-700">Live</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{t.description}</p>
            <p className="text-xs text-slate-400 mt-0.5">Trigger: {t.trigger}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => onPreview(t.id)} data-testid={`preview-${t.id}`}>
              <Eye className="w-3.5 h-3.5 mr-1" /> Preview
            </Button>
            <Button size="sm" variant="outline" onClick={() => onTestSend(t.id)} disabled={sending === t.id} data-testid={`test-send-${t.id}`}>
              <Send className="w-3.5 h-3.5 mr-1" /> {sending === t.id ? 'Sending...' : 'Test'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Template Preview ────────────────────────────────────
function TemplatePreview({ templateId, html, subject, onClose, api }) {
  const [mode, setMode] = useState('desktop');
  const [sendingPreview, setSendingPreview] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (iframeRef.current && html) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(html);
      doc.close();
    }
  }, [html, mode]);

  if (!templateId) return null;

  // Mobile scaling constants
  const PHONE_W = 375;
  const PHONE_PAD_X = 6;
  const SCREEN_W = PHONE_W - PHONE_PAD_X * 2; // 363
  const EMAIL_W = 600;
  const SCALE = SCREEN_W / EMAIL_W; // ~0.605
  const IFRAME_H = 1600;
  const VISUAL_H = Math.ceil(IFRAME_H * SCALE);

  return (
    <div className="space-y-4" data-testid="template-preview">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <p className="font-medium text-sm text-slate-900">Subject: {subject}</p>
          <p className="text-xs text-slate-500">Template: {templateId}</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          <Button size="sm" variant={mode === 'desktop' ? 'default' : 'ghost'} className="h-7 text-xs" onClick={() => setMode('desktop')} data-testid="preview-desktop-btn">
            <Monitor className="w-3.5 h-3.5 mr-1" /> Desktop
          </Button>
          <Button size="sm" variant={mode === 'mobile' ? 'default' : 'ghost'} className="h-7 text-xs" onClick={() => setMode('mobile')} data-testid="preview-mobile-btn">
            <Smartphone className="w-3.5 h-3.5 mr-1" /> Mobile
          </Button>
        </div>
      </div>

      <div className="flex justify-center">
        {mode === 'desktop' ? (
          <div className="bg-slate-200 rounded-xl p-2 w-full max-w-[700px]">
            <div className="bg-white rounded-lg overflow-hidden">
              <iframe
                ref={iframeRef}
                title="Email Preview"
                className="w-full border-0"
                style={{ height: '500px' }}
                sandbox="allow-same-origin"
                data-testid="preview-iframe"
              />
            </div>
          </div>
        ) : (
          <div
            className="bg-slate-200"
            style={{ width: PHONE_W, borderRadius: '2rem', padding: '14px 6px' }}
            data-testid="mobile-phone-frame"
          >
            {/* Notch */}
            <div className="flex justify-center mb-2">
              <div className="w-24 h-1.5 bg-slate-400/50 rounded-full" />
            </div>
            {/* Screen — scrollable */}
            <div
              className="bg-white"
              style={{ borderRadius: '1.2rem', height: 580, overflowY: 'auto', overflowX: 'hidden' }}
              data-testid="mobile-phone-screen"
            >
              <div style={{ width: SCREEN_W, height: VISUAL_H, position: 'relative' }}>
                <iframe
                  ref={iframeRef}
                  title="Email Preview"
                  style={{
                    width: EMAIL_W,
                    height: IFRAME_H,
                    transform: `scale(${SCALE})`,
                    transformOrigin: 'top left',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    border: 'none',
                  }}
                  sandbox="allow-same-origin"
                  data-testid="preview-iframe"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Button variant="outline" size="sm" onClick={onClose} className="w-full sm:w-auto">
          Back to Templates
        </Button>
        <Button
          size="sm"
          variant="default"
          className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003a6d]"
          disabled={sendingPreview}
          data-testid="send-preview-inbox-btn"
          onClick={async () => {
            setSendingPreview(true);
            try {
              const res = await api.post('/platform-owner/email/test-send', { template_id: templateId });
              if (res.data.success) {
                toast.success('Preview sent', { description: `Check ${res.data.sent_to}` });
              } else {
                toast.error('Send failed', { description: res.data.error || 'Unknown error' });
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Failed to send preview');
            }
            setSendingPreview(false);
          }}
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {sendingPreview ? 'Sending...' : 'Send Preview to My Inbox'}
        </Button>
      </div>
    </div>
  );
}

// ─── Sender Settings ─────────────────────────────────────
function SenderSettings({ api }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/platform-owner/email/sender').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/platform-owner/email/sender', data);
      setData(res.data);
      toast.success('Sender settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  if (!data) return <div className="animate-pulse h-40 bg-slate-100 rounded-lg" />;

  const fields = [
    { key: 'from_email', label: 'From Email (SendGrid Verified)', editable: false, hint: 'Controlled by SendGrid verified sender identity' },
    { key: 'from_name', label: 'Sender Display Name', editable: true },
    { key: 'reply_to', label: 'Reply-To Email', editable: true },
    { key: 'support_email', label: 'Support Email', editable: true },
    { key: 'legal_email', label: 'Legal Email', editable: true },
  ];

  return (
    <div className="space-y-4" data-testid="sender-settings">
      {fields.map(f => (
        <div key={f.key} className="space-y-1.5">
          <Label className="text-sm font-medium">{f.label}</Label>
          {f.hint && <p className="text-xs text-slate-400">{f.hint}</p>}
          <Input
            value={data[f.key] || ''}
            onChange={e => setData(prev => ({ ...prev, [f.key]: e.target.value }))}
            disabled={!f.editable}
            className={`text-sm ${!f.editable ? 'bg-slate-50 text-slate-500' : ''}`}
            data-testid={`sender-${f.key}`}
          />
        </div>
      ))}
      <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003a6d]" data-testid="save-sender-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Sender Settings'}
      </Button>
    </div>
  );
}

// ─── Branding Settings ───────────────────────────────────
function BrandingSettings({ api }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/platform-owner/email/branding').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/platform-owner/email/branding', data);
      setData(res.data);
      toast.success('Branding saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/platform-owner/email/branding/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setData(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  if (!data) return <div className="animate-pulse h-40 bg-slate-100 rounded-lg" />;

  return (
    <div className="space-y-4" data-testid="branding-settings">
      {/* Logo section */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Logo</Label>
        <p className="text-xs text-slate-400">Displayed in the email header. Upload an image or paste a hosted URL (PNG/JPG/SVG, max 2MB).</p>

        {/* Upload area */}
        <div
          className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex flex-col items-center gap-2 hover:border-slate-300 transition-colors cursor-pointer"
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="logo-upload-area"
        >
          {data.logo_url ? (
            <img src={data.logo_url} alt="Current logo" style={{ maxHeight: '48px', maxWidth: '200px' }} onError={e => { e.target.style.display = 'none'; }} data-testid="logo-preview-img" />
          ) : (
            <Image className="w-8 h-8 text-slate-300" />
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" type="button" disabled={uploading} className="pointer-events-none" data-testid="upload-logo-btn">
              <Image className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </Button>
            {data.logo_url && <span className="text-xs text-green-600 font-medium">Logo set</span>}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={handleLogoUpload}
            data-testid="logo-file-input"
          />
        </div>

        {/* Manual URL fallback */}
        <div className="pt-1">
          <p className="text-xs text-slate-400 mb-1">Or paste a URL directly:</p>
          <Input value={data.logo_url || ''} onChange={e => setData(prev => ({ ...prev, logo_url: e.target.value }))} placeholder="https://..." className="text-sm" data-testid="branding-logo-url" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Business Name</Label>
        <Input value={data.business_name || ''} onChange={e => setData(prev => ({ ...prev, business_name: e.target.value }))} className="text-sm" data-testid="branding-business-name" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Tagline</Label>
        <Input value={data.tagline || ''} onChange={e => setData(prev => ({ ...prev, tagline: e.target.value }))} className="text-sm" data-testid="branding-tagline" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Footer Text</Label>
        <Input value={data.footer_text || ''} onChange={e => setData(prev => ({ ...prev, footer_text: e.target.value }))} className="text-sm" data-testid="branding-footer" />
      </div>
      <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003a6d]" data-testid="save-branding-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Branding'}
      </Button>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────
export default function EmailCentre() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [sending, setSending] = useState(null);
  const [sgConfigured, setSgConfigured] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [poTestSending, setPoTestSending] = useState(false);

  useEffect(() => {
    api.get('/platform-owner/email/templates').then(r => {
      setTemplates(r.data.templates || []);
      setSgConfigured(r.data.sendgrid_configured);
    }).catch(() => {});
    api.get('/admin/email-status').then(r => setEmailStatus(r.data)).catch(() => {});
  }, [api]);

  const refreshEmailLogs = async () => {
    setLogsLoading(true);
    try {
      const r = await api.get('/admin/email-logs', { params: { limit: 25 } });
      setEmailLogs(r.data.logs || []);
    } catch (e) { /* ignore */ }
    setLogsLoading(false);
  };

  useEffect(() => { refreshEmailLogs(); /* eslint-disable-next-line */ }, []);

  const handlePoTestSend = async () => {
    setPoTestSending(true);
    try {
      const res = await api.post('/admin/test-email', { template: 'test' });
      if (res.data.success && !res.data.error) {
        toast.success(`Test email sent to ${res.data.to_email}`);
      } else {
        toast.error(res.data.error || 'Send failed');
      }
      refreshEmailLogs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send test'); }
    setPoTestSending(false);
  };

  const handlePreview = async (templateId) => {
    try {
      const res = await api.get(`/platform-owner/email/templates/${templateId}/preview`);
      setPreviewData(res.data);
    } catch (e) { toast.error('Failed to load preview'); }
  };

  const handleTestSend = async (templateId) => {
    setSending(templateId);
    try {
      const res = await api.post('/platform-owner/email/test-send', { template_id: templateId });
      if (res.data.success) {
        toast.success(`Test email sent to ${res.data.sent_to}`);
      } else {
        toast.error(res.data.error || 'Send failed');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send test'); }
    setSending(null);
  };

  return (
    <PlatformOwnerAppShell activeId="email-centre" title="Email Centre">
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6" data-testid="email-centre-page">
        {/* Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#004B8D]" />
            Email Centre
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage outbound email templates, sender identity, and branding.</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="secondary" className={sgConfigured ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
              SendGrid {sgConfigured ? 'Connected' : 'Not Configured'}
            </Badge>
            <Badge variant="secondary">{templates.length} templates</Badge>
            {emailStatus?.email_mode && (
              <Badge variant="secondary" className={emailStatus.is_restricted ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-700'} data-testid="email-mode-badge">
                Mode: {emailStatus.email_mode}
              </Badge>
            )}
          </div>
        </div>

        {/* Iter119 — Restricted-mode banner */}
        {emailStatus?.is_restricted && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="restricted-mode-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">🟡 Email Mode: Restricted (Safe Testing Mode)</p>
              <p className="text-xs text-amber-700 mt-1">
                Real emails are only delivered to the allowlist below. All other sends are recorded in the email logs as <code className="px-1 py-0.5 bg-amber-100 rounded">blocked_due_to_mode</code>.
                Booking, payment, and verification emails do <strong>not</strong> trigger external delivery while this mode is active.
              </p>
              <p className="text-xs text-amber-700 mt-1">
                <strong>Allowlist:</strong> {(emailStatus.restricted_allowlist || []).join(', ') || '(none)'}
              </p>
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={handlePoTestSend}
                  disabled={poTestSending || !sgConfigured}
                  data-testid="send-test-email-po-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {poTestSending ? 'Sending…' : 'Send Test Email to Platform Owner'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {!sgConfigured && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">SendGrid not configured</p>
              <p className="text-xs text-amber-600 mt-0.5">Test sends will not work until SENDGRID_API_KEY is set in the environment.</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="templates" className="space-y-4">
          <TabsList className="w-full sm:w-auto grid grid-cols-4 sm:inline-flex">
            <TabsTrigger value="templates" className="text-xs sm:text-sm" data-testid="tab-templates">
              <FileText className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" /> Templates
            </TabsTrigger>
            <TabsTrigger value="sender" className="text-xs sm:text-sm" data-testid="tab-sender">
              <Settings className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" /> Sender
            </TabsTrigger>
            <TabsTrigger value="branding" className="text-xs sm:text-sm" data-testid="tab-branding">
              <Palette className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" /> Branding
            </TabsTrigger>
            <TabsTrigger value="logs" className="text-xs sm:text-sm" data-testid="tab-logs">
              <FileText className="w-3.5 h-3.5 mr-1.5 hidden sm:inline" /> Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#004B8D]" />
                  Email Templates
                </CardTitle>
                <p className="text-xs text-slate-500">All transactional email templates currently used by HireHub. Templates are code-managed — subjects and content are editable in future releases.</p>
              </CardHeader>
              <CardContent>
                {previewData ? (
                  <TemplatePreview
                    templateId={previewData.template_id}
                    html={previewData.html}
                    subject={previewData.subject}
                    onClose={() => setPreviewData(null)}
                    api={api}
                  />
                ) : (
                  <TemplateList templates={templates} onPreview={handlePreview} onTestSend={handleTestSend} sending={sending} />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sender">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="w-4 h-4 text-[#004B8D]" />
                  Sender Identity
                </CardTitle>
                <p className="text-xs text-slate-500">Configure the sender details used in all outbound emails. The "From Email" is controlled by SendGrid verified sender identity and cannot be changed here.</p>
              </CardHeader>
              <CardContent>
                <SenderSettings api={api} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Palette className="w-4 h-4 text-[#004B8D]" />
                  Email Branding
                </CardTitle>
                <p className="text-xs text-slate-500">Customize the logo, business name, and footer shown in all email templates. Changes apply to previews immediately and to real sends when the branding integration is enabled.</p>
              </CardHeader>
              <CardContent>
                <BrandingSettings api={api} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#004B8D]" />
                      Email Logs
                    </CardTitle>
                    <p className="text-xs text-slate-500">Recent outbound email events. Includes sent, blocked (restricted mode), and failed deliveries.</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={refreshEmailLogs} disabled={logsLoading} data-testid="email-logs-refresh-btn">
                    {logsLoading ? 'Refreshing…' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {emailLogs.length === 0 ? (
                  <div className="text-sm text-slate-500 text-center py-8" data-testid="email-logs-empty">
                    No email events recorded yet. Send a test email to see logs.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs" data-testid="email-logs-table">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-slate-700 font-semibold">Time</th>
                          <th className="text-left py-2 px-3 text-slate-700 font-semibold">Recipient</th>
                          <th className="text-left py-2 px-3 text-slate-700 font-semibold">Template</th>
                          <th className="text-left py-2 px-3 text-slate-700 font-semibold">Trigger</th>
                          <th className="text-left py-2 px-3 text-slate-700 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log, idx) => {
                          const statusStyle = log.status === 'sent' ? 'bg-green-50 text-green-700 border-green-200'
                            : log.status === 'blocked_due_to_mode' ? 'bg-amber-50 text-amber-800 border-amber-200'
                            : log.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-slate-50 text-slate-700 border-slate-200';
                          return (
                            <tr key={idx} className="border-b border-slate-100" data-testid={`email-log-row-${idx}`}>
                              <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : '—'}</td>
                              <td className="py-2 px-3 text-slate-900 font-mono">{log.recipient || '—'}</td>
                              <td className="py-2 px-3 text-slate-700">{log.template || '—'}</td>
                              <td className="py-2 px-3 text-slate-500">{log.trigger_source || '—'}</td>
                              <td className="py-2 px-3">
                                <Badge variant="secondary" className={`text-[10px] ${statusStyle}`}>{log.status}</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PlatformOwnerAppShell>
  );
}
